import { useEffect, useState } from "react";
import type {
  BookExposureView,
  DashboardSnapshot,
  DashboardSport,
  RecommendationView,
} from "../api/client.js";
import {
  LineupQueue,
  type LineupCandidateView,
} from "../components/lineup-queue.js";
import { OfferDrawer } from "../components/offer-drawer.js";
import { PlaceBetDialog } from "../components/place-bet-dialog.js";
import { RecommendationsTable } from "../components/recommendations-table.js";
import { Figure, KpiStrip, Tile, money } from "../components/tile.js";
import type {
  PrototypeLineupCandidate,
  PrototypeLineupDecision,
} from "../prototype/types.js";

const books = [
  "sportsbet",
  "bet365",
  "tab",
  "pointsbet",
  "ladbrokes",
  "dabble",
  "picklebet",
  "betr",
  "nextbet",
  "unibet",
  "betright",
];
const defaultRecommendation: RecommendationView = {
  signalId: "demo-signal-1",
  melbourneTip: "11:00 AM AEDT",
  ruleVersion: "THREE_PM_REVERSION_v1.0",
  description: "Example Player Under 2.5 3PM",
  gameLabel: "BOS @ NYK",
  bookmaker: "sportsbet",
  line: 2.5,
  price: 1.91,
  minimumOdds: 1.84,
  units: 0.5,
  stakeCents: 1000,
  status: "RECOMMENDED",
  evidence: [
    "Previous game: 31 minutes",
    "Previous 3PM: 1",
    "AU reference line: 2.5",
  ],
  offers: [
    ...books.map((bookmaker, index) => ({
      bookmaker,
      price: 1.82 + index * 0.01,
      line: 2.5,
      role: "AU_EXECUTION" as const,
      url: null,
    })),
    {
      bookmaker: "draftkings",
      price: 1.86,
      line: 2.5,
      role: "US_REFERENCE",
      url: null,
    },
  ],
};
export const defaultSnapshot: DashboardSnapshot = {
  bankrollCents: 1_000_000,
  unitCents: 2_000,
  committedCents: 0,
  recommendedCents: 1_000,
  replayReady: false,
  recommendations: [defaultRecommendation],
};

/** How long a deep-linked bet may take to appear before the page says why. */
export const DEEP_LINK_GRACE_MS = 10_000;

export function TodayPage({
  snapshot = defaultSnapshot,
  onPlaceBet,
  onSkipBet,
  openPlacementFor = null,
  onPlacementOpened,
  lineups = [],
  onLineupDecision,
  prototype = false,
  sport = "NBA",
  books = [],
}: {
  snapshot?: DashboardSnapshot;
  /** Account state per book, so the placement dialog can spread the wear
   * rather than sending every bet to whoever is a cent better. */
  books?: readonly BookExposureView[];
  /** Natural key or signal id of a bet to open the placement dialog for,
   * arriving from a Discord deep link. */
  openPlacementFor?: string | null;
  onPlacementOpened?: () => void;
  onSkipBet?: (recommendation: RecommendationView) => void;
  onPlaceBet?: (
    recommendation: RecommendationView,
    input: {
      actualLine: number | null;
      actualPrice: number;
      stakeCents: number;
      bookmaker: string;
    },
  ) => Promise<void> | void;
  lineups?: readonly (PrototypeLineupCandidate | LineupCandidateView)[];
  onLineupDecision?: (
    candidateId: string,
    status: PrototypeLineupDecision,
  ) => void;
  prototype?: boolean;
  sport?: DashboardSport | "ALL";
}) {
  // Tracked rules stake $0 and need no action, so Today shows live bets only
  // (Moss, 10 Oct 2026). Tracked rows stay on the ledger and rule pages.
  const recommendations = snapshot.recommendations.filter(
    (recommendation) => (recommendation.executionMode ?? "LIVE") === "LIVE",
  );
  const [selected, setSelected] = useState<RecommendationView | null>(
    recommendations[0] ?? null,
  );
  const [placement, setPlacement] = useState<RecommendationView | null>(null);
  // Open the dialog for a deep-linked bet as soon as it appears in the
  // snapshot. The recommendations arrive asynchronously, so this cannot run
  // only on mount.
  useEffect(() => {
    if (openPlacementFor === null) return;
    const target = snapshot.recommendations.find(
      (row) =>
        row.naturalKey === openPlacementFor ||
        row.signalId === openPlacementFor,
    );
    if (target === undefined) return;
    setPlacement(target);
    setSelected(target);
    onPlacementOpened?.();
  }, [openPlacementFor, onPlacementOpened, snapshot.recommendations]);
  // A link to a bet that is no longer on the board used to open the page and
  // do nothing. The board only holds bets whose game has not started, so say
  // so. Waits out the first refresh, since recommendations arrive late.
  const [linkMissed, setLinkMissed] = useState(false);
  useEffect(() => {
    if (openPlacementFor === null) return;
    const timer = setTimeout(() => {
      setLinkMissed(true);
    }, DEEP_LINK_GRACE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [openPlacementFor]);
  const qualified = recommendations.filter(
    (recommendation) => recommendation.status === "RECOMMENDED",
  ).length;
  return (
    <div className="board-grid today-board">
      {linkMissed && openPlacementFor !== null ? (
        <div className="prototype-banner" role="alert">
          <strong>
            That bet can no longer be logged here: its game has started or it
            was withdrawn. If you placed it before the start, send Claude the
            price and stake.
          </strong>
          <button
            type="button"
            onClick={() => {
              setLinkMissed(false);
              onPlacementOpened?.();
            }}
          >
            Dismiss
          </button>
        </div>
      ) : null}
      <KpiStrip columns={6}>
        <Figure label="Bankroll" value={money(snapshot.bankrollCents)} />
        <Figure
          label="Unit"
          value={money(snapshot.unitCents)}
          sub="Frozen per slate"
        />
        <Figure
          label="At risk"
          value={money(snapshot.committedCents)}
          {...(snapshot.committedCents > 0 ? { tone: "warning" as const } : {})}
        />
        <Figure
          label="Recommended"
          value={money(snapshot.recommendedCents)}
          sub={`${qualified} qualified`}
        />
        <Figure
          label="Bet alerts"
          value={snapshot.replayReady ? "Enabled" : "Blocked"}
          tone={snapshot.replayReady ? "positive" : "warning"}
          sub={
            snapshot.replayReady
              ? "Replay approved"
              : "Replay approval required"
          }
        />
        <Figure
          label="Mode"
          value={prototype ? "Rehearsal" : "Live"}
          {...(prototype
            ? { tone: "warning" as const }
            : { tone: "positive" as const })}
          sub={prototype ? "No real bets" : "Real money"}
        />
      </KpiStrip>

      <Tile
        title="Qualified signals"
        note="DraftKings excluded from every decision"
        span={9}
      >
        <RecommendationsTable
          showSport={sport === "ALL"}
          recommendations={recommendations}
          selectedId={selected?.signalId ?? null}
          onSelect={setSelected}
          onPlace={setPlacement}
          {...(onSkipBet === undefined ? {} : { onSkip: onSkipBet })}
          books={books}
        />
      </Tile>

      <div className="board-side">
        <LineupQueue
          candidates={lineups}
          {...(onLineupDecision === undefined
            ? {}
            : { onDecision: onLineupDecision })}
        />
      </div>

      <div className="board-wide">
        <OfferDrawer recommendation={selected} />
      </div>
      {placement === null ? null : (
        <PlaceBetDialog
          recommendation={placement}
          books={books}
          onClose={() => setPlacement(null)}
          onConfirm={async (input) => {
            await onPlaceBet?.(placement, input);
          }}
        />
      )}
    </div>
  );
}
